import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser } from 'aws-amplify/auth';
import ApiServices from '../../shared/ApiServices/ApiServices';

interface MeData {
    user_id: string;
    role: string;
}

interface UserContextProps {
    user: any;
    setUser: React.Dispatch<React.SetStateAction<any>>;
    userRole: string | null;
    userMe: MeData | null;
    roleLoading: boolean;
    refreshMe: () => Promise<void>;
}

export const UserContext = createContext<UserContextProps | undefined>(undefined);

export const useUser = (): UserContextProps => {
    const context = useContext(UserContext);
    if (!context) throw new Error('useUser must be used within a UserProvider');
    return context;
};

const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<any>(null);
    const [userMe, setUserMe] = useState<MeData | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [roleLoading, setRoleLoading] = useState(true);

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const fetchMe = useCallback(async () => {
        if (!mountedRef.current) return;
        setRoleLoading(true);
        try {
            const me = await ApiServices.getMe();
            if (mountedRef.current) {
                setUserMe(me);
                setUserRole(me?.role ?? null);
            }
        } catch (err) {
            console.warn('[UserContext] GET /me failed:', err);
            if (mountedRef.current) {
                setUserMe(null);
                setUserRole(null);
            }
        } finally {
            if (mountedRef.current) setRoleLoading(false);
        }
    }, []);

    const refreshMe = useCallback(async () => {
        await fetchMe();
    }, [fetchMe]);

    useEffect(() => {
        const init = async () => {
            try {
                const authData = await getCurrentUser();
                if (authData && mountedRef.current) {
                    setUser(authData);
                    await fetchMe();
                }
            } catch {
            }
        };
        init();
    }, [fetchMe]);

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', async ({ payload }) => {
            switch (payload.event) {
                case 'signedIn':
                    if (mountedRef.current) setUser(payload.data);
                    await fetchMe();
                    break;

                case 'signedOut':
                    if (mountedRef.current) {
                        setUser(null);
                        setUserMe(null);
                        setUserRole(null);
                    }
                    break;

                default:
                    break;
            }
        });

        return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
    }, [fetchMe]);

    return (
        <UserContext.Provider value={{ user, setUser, userRole, userMe, roleLoading, refreshMe }}>
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
